import { ClosureSignal, clamp01, opennessOf } from "./closure.ts";
import type { EventBus } from "./eventBus.ts";
import { createFilter, type Filter } from "./filters.ts";
import type { BlinkEvent, DetectorConfig, Eye, EyePair, SignalFrame } from "./types.ts";

export type BlinkState = "open" | "closed" | "no-face";

export interface DetectorTick {
  t: number;
  /** Filtered, normalised, both eyes combined. This is what the machine decides on. */
  closure: number;
  /** Same value before smoothing — plotted alongside to make the filter's cost visible. */
  closureRaw: number;
  perEye: EyePair<number>;
  state: BlinkState;
  /** False during baseline warm-up; no events are emitted until true. */
  ready: boolean;
  lowConfidence: boolean;
}

interface ClosurePhase {
  startedAt: number;
  peak: number;
  peakAt: number;
  peakPerEye: EyePair<number>;
  frames: number;
  lowConfidenceFrames: number;
}

/**
 * Why blinks did or did not come out. Purely diagnostic, but it is the
 * difference between an app that says "nothing detected" and one that says
 * which stage ate the signal — which is all that separates a useful bug report
 * from a shrug when the failure is on someone else's phone.
 */
export interface DetectorStats {
  framesSeen: number;
  framesWithFace: number;
  framesLowConfidence: number;
  closuresStarted: number;
  suppressedByConfidence: number;
  suppressedByDuration: number;
  /** Closures abandoned for outlasting maxDurationMs. Previously uncounted. */
  abandonedLong: number;
  emitted: number;
  /** Highest closure value seen since reset — 0 means the signal never moved. */
  peakClosure: number;
}

/** EMA weight for the pose used in confidence gating. See #assessConfidence. */
const POSE_SMOOTHING = 0.35;

const SQUINT_THRESHOLD = 0.4;
const SQUINT_SUSTAIN_MS = 1500;

/**
 * The state machine of spec §4, plus the parts the spec leaves implicit:
 * confidence gating, warm-up, and per-event latency accounting.
 *
 * Deliberately knows nothing about cameras, canvases or games. It consumes
 * SignalFrames — which a live camera and a recorded fixture produce identically
 * — and emits onto the bus. That symmetry is the whole reason a threshold change
 * can be evaluated by `npm run eval` instead of by sitting in front of a webcam.
 */
export class BlinkDetector {
  #config: DetectorConfig;
  readonly #bus: EventBus;

  #left: ClosureSignal;
  #right: ClosureSignal;
  #filter: Filter;

  #state: BlinkState = "no-face";
  #phase: ClosurePhase | null = null;
  /**
   * Set when a closure is abandoned for running too long. Blocks a new closure
   * from starting until the eyes have actually reopened — without it, resting
   * your eyes for five seconds abandons one phase, immediately starts another
   * while still shut, and reports a blink the moment you open them.
   */
  #awaitingReopen = false;
  #lastBlinkAt: number | null = null;
  #noBlinkAlerted = false;
  #smoothedPose: SignalFrame["pose"] | null = null;
  #squintSince: number | null = null;
  #squintReported = false;
  #stats: DetectorStats = emptyStats();

  constructor(config: DetectorConfig, bus: EventBus) {
    this.#config = config;
    this.#bus = bus;
    this.#filter = createFilter(config.filter);
    this.#left = new ClosureSignal(config.signal, config.closedFraction);
    this.#right = new ClosureSignal(config.signal, config.closedFraction);
  }

  get config(): DetectorConfig {
    return this.#config;
  }

  get stats(): DetectorStats {
    return { ...this.#stats };
  }

  /**
   * Swaps configuration mid-stream. Used by the UI's A/B toggle and by the eval
   * sweep; both re-run from the start of a recording afterwards, so dropping the
   * filter and baseline state here is correct rather than merely convenient.
   */
  reconfigure(config: DetectorConfig): void {
    this.#config = config;
    this.#filter = createFilter(config.filter);
    this.#left = new ClosureSignal(config.signal, config.closedFraction);
    this.#right = new ClosureSignal(config.signal, config.closedFraction);
    this.reset();
  }

  reset(): void {
    this.#left.reset();
    this.#right.reset();
    this.#filter.reset();
    this.#state = "no-face";
    this.#phase = null;
    this.#awaitingReopen = false;
    this.#lastBlinkAt = null;
    this.#noBlinkAlerted = false;
    this.#smoothedPose = null;
    this.#squintSince = null;
    this.#squintReported = false;
    this.#stats = emptyStats();
  }

  push(frame: SignalFrame): DetectorTick {
    this.#stats.framesSeen++;
    if (!frame.faceFound) return this.#handleFaceLost(frame);
    this.#stats.framesWithFace++;

    if (this.#state === "no-face") {
      this.#bus.emit({ type: "face-found", t: frame.t });
      this.#state = "open";
      this.#lastBlinkAt ??= frame.t;
    }

    const lowConfidence = this.#assessConfidence(frame);
    const { signal } = this.#config;

    const perEye: EyePair<number> = {
      left: this.#left.push(opennessOf(frame, signal, "left")),
      right: this.#right.push(opennessOf(frame, signal, "right")),
    };

    const closureRaw = meanOfFinite(perEye.left, perEye.right);
    const closure = clamp01(this.#filter.push(closureRaw, frame.t));
    const ready = this.#left.ready && this.#right.ready;

    if (lowConfidence) this.#stats.framesLowConfidence++;
    if (Number.isFinite(closure) && closure > this.#stats.peakClosure) {
      this.#stats.peakClosure = closure;
    }

    if (ready) {
      this.#advance(frame, closure, perEye, lowConfidence);
      this.#checkNoBlink(frame);
      this.#checkSquint(frame);
    }

    return {
      t: frame.t,
      closure,
      closureRaw,
      perEye,
      state: this.#state,
      ready,
      lowConfidence,
    };
  }

  // -------------------------------------------------------------------------

  #advance(
    frame: SignalFrame,
    closure: number,
    perEye: EyePair<number>,
    lowConfidence: boolean,
  ): void {
    const cfg = this.#config;

    if (this.#phase === null) {
      if (this.#awaitingReopen) {
        // Clear at closeThreshold, not openThreshold.
        //
        // The latch exists so an abandoned long closure is not immediately
        // re-detected as a fresh one. Below closeThreshold a new closure cannot
        // start anyway, so that job is done — demanding a return to nearly-open
        // adds nothing and can wedge the machine indefinitely.
        //
        // Which is exactly what happened on real data: one session had a
        // 16.7-second stretch with 21 distinct eyelid excursions and ZERO
        // detections, because that subject's resting closure sits around 0.33,
        // above the 0.25 reopen threshold. The eye never looked "open enough"
        // to re-arm. Disabling the latch recovered six detections in that window.
        if (closure < cfg.closeThreshold) this.#awaitingReopen = false;
        return;
      }
      // Never *start* a closure on an untrusted frame. A fast head turn
      // foreshortens the eye and looks exactly like the beginning of a blink;
      // this is the single cheapest false-positive guard available (spec §9).
      if (closure > cfg.closeThreshold && !lowConfidence) {
        this.#state = "closed";
        this.#stats.closuresStarted++;
        this.#phase = {
          startedAt: frame.t,
          peak: closure,
          peakAt: frame.t,
          peakPerEye: { ...perEye },
          frames: 1,
          lowConfidenceFrames: 0,
        };
      }
      return;
    }

    const phase = this.#phase;
    phase.frames++;
    if (lowConfidence) phase.lowConfidenceFrames++;
    if (closure > phase.peak) {
      phase.peak = closure;
      phase.peakAt = frame.t;
      phase.peakPerEye = { ...perEye };
    }

    if (closure >= cfg.openThreshold) {
      // Still shut. A closure that outlasts maxDuration is resting, not blinking.
      if (frame.t - phase.startedAt > cfg.maxDurationMs) {
        this.#phase = null;
        this.#state = "open";
        this.#awaitingReopen = true;
        // Counted, because it was not: abandonment happens on this path and
        // incremented nothing, so a detector that had silently stopped emitting
        // looked identical in the stats to one with nothing to report.
        this.#stats.abandonedLong++;
      }
      return;
    }

    // Reopened.
    const duration = frame.t - phase.startedAt;
    this.#phase = null;
    this.#state = "open";

    if (duration < cfg.minDurationMs || duration > cfg.maxDurationMs) {
      this.#stats.suppressedByDuration++;
      return;
    }

    // A *majority* of untrusted frames, not merely one.
    //
    // Rejecting a blink because any single frame in it was jittery is fine on a
    // tripod and catastrophic on a handheld phone, where the camera moves
    // relative to the face continuously — it rejects essentially every blink,
    // and reports nothing at all rather than reporting a problem. A sustained
    // head turn still trips this, because then most of the closure is untrusted,
    // which is the case the gate actually exists for.
    if (phase.lowConfidenceFrames * 2 > phase.frames) {
      this.#stats.suppressedByConfidence++;
      return;
    }

    const kind = phase.peak >= cfg.completeAmplitude ? "complete" : "partial";
    const event: BlinkEvent = {
      type: "blink",
      t: frame.t,
      startedAt: phase.startedAt,
      duration,
      amplitude: phase.peak,
      kind,
      eye: attributeEye(phase.peakPerEye, cfg.completeAmplitude),
      // Everything between the deepest sample and the decision is cost we added:
      // filter group delay, hysteresis, and frame quantisation.
      latency: frame.t - phase.peakAt,
    };

    if (kind === "complete") {
      this.#lastBlinkAt = frame.t;
      this.#noBlinkAlerted = false;
    }
    this.#stats.emitted++;
    this.#bus.emit(event);
  }

  #handleFaceLost(frame: SignalFrame): DetectorTick {
    if (this.#state !== "no-face") {
      this.#bus.emit({ type: "face-lost", t: frame.t });
      this.#state = "no-face";
      this.#phase = null;
      this.#filter.reset();
    }
    return {
      t: frame.t,
      closure: Number.NaN,
      closureRaw: Number.NaN,
      perEye: { left: Number.NaN, right: Number.NaN },
      state: "no-face",
      ready: false,
      lowConfidence: true,
    };
  }

  /**
   * Decides whether this frame can be trusted for blink decisions.
   *
   * The pose delta is measured on a *smoothed* pose, not on the raw frame-to-
   * frame difference. That distinction is the whole thing: hand tremor is
   * zero-mean noise, so it produces large instantaneous deltas that mean nothing
   * and vanish under smoothing, while a head turn moves the smoothed pose
   * steadily and still trips the gate. Comparing raw consecutive frames cannot
   * tell those apart, so on a handheld phone it marks most frames untrusted and
   * the detector simply goes quiet.
   */
  #assessConfidence(frame: SignalFrame): boolean {
    const cfg = this.#config;
    let low = frame.lowConfidence || Math.abs(frame.pose.yaw) > cfg.maxYaw;

    const previous = this.#smoothedPose;
    const smoothed: SignalFrame["pose"] = previous
      ? {
          pitch: previous.pitch + POSE_SMOOTHING * (frame.pose.pitch - previous.pitch),
          yaw: previous.yaw + POSE_SMOOTHING * (frame.pose.yaw - previous.yaw),
          roll: previous.roll + POSE_SMOOTHING * (frame.pose.roll - previous.roll),
        }
      : { ...frame.pose };

    if (previous) {
      const delta =
        Math.abs(smoothed.yaw - previous.yaw) + Math.abs(smoothed.pitch - previous.pitch);
      if (delta > cfg.maxPoseDelta) low = true;
    }

    this.#smoothedPose = smoothed;
    return low;
  }

  #checkNoBlink(frame: SignalFrame): void {
    if (this.#lastBlinkAt === null || this.#noBlinkAlerted) return;
    const elapsed = frame.t - this.#lastBlinkAt;
    if (elapsed >= this.#config.noBlinkAfterMs) {
      this.#noBlinkAlerted = true;
      this.#bus.emit({ type: "no-blink", t: frame.t, elapsed });
    }
  }

  #checkSquint(frame: SignalFrame): void {
    const intensity = meanOfFinite(frame.squint.left, frame.squint.right);
    if (!(intensity > SQUINT_THRESHOLD)) {
      this.#squintSince = null;
      this.#squintReported = false;
      return;
    }
    this.#squintSince ??= frame.t;
    const duration = frame.t - this.#squintSince;
    if (duration >= SQUINT_SUSTAIN_MS && !this.#squintReported) {
      this.#squintReported = true;
      this.#bus.emit({ type: "squint", t: frame.t, duration, intensity });
    }
  }
}

function emptyStats(): DetectorStats {
  return {
    framesSeen: 0,
    framesWithFace: 0,
    framesLowConfidence: 0,
    closuresStarted: 0,
    suppressedByConfidence: 0,
    suppressedByDuration: 0,
    abandonedLong: 0,
    emitted: 0,
    peakClosure: 0,
  };
}

function meanOfFinite(a: number, b: number): number {
  const aOk = Number.isFinite(a);
  const bOk = Number.isFinite(b);
  if (aOk && bOk) return (a + b) / 2;
  if (aOk) return a;
  if (bOk) return b;
  return Number.NaN;
}

/**
 * Which eye actually closed. Mostly "both" — but a one-eyed closure is either a
 * wink (a possible game input) or a landmark failure on the far eye, and those
 * are worth being able to tell apart in the logs.
 */
function attributeEye(peak: EyePair<number>, threshold: number): Eye {
  const left = peak.left >= threshold;
  const right = peak.right >= threshold;
  if (left && right) return "both";
  if (left) return "left";
  if (right) return "right";
  return "both";
}
